import React, { useState, useEffect } from "react";
import { Layout, Badge, Dropdown, Menu, Modal, message } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import "./Header.css";

const { Header } = Layout;

/**
 * AppHeader with framer‑motion animations and formatted notifications support
 * 1. Page title animates on route change.
 * 2. Bell icon spins while notifications are loading.
 * 3. Dropdown list for notifications with formatted text support.
 * 4. Modal displays formatted notifications properly.
 */
const AppHeader = () => {
  const logoSrc = require("../assets/logo.png");
  const location = useLocation();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeNotification, setActiveNotification] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  /* -------------------------------------------------------------------- */
  /* Text formatting helper                                               */
  /* -------------------------------------------------------------------- */
  const formatNotificationText = (text) => {
    if (!text) return '';
    
    return text
      // Жирный текст
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Курсив
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Подчеркивание
      .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>')
      // Переводы строк
      .replace(/\n/g, '<br/>');
  };

  // Функция для получения превью текста (без HTML тегов)
  const getTextPreview = (text, maxLength = 60) => {
    if (!text) return 'Bez treści';
    
    // Убираем markdown разметку для превью
    const plainText = text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .replace(/<u>(.*?)<\/u>/g, '$1')
      .replace(/\n/g, ' ');
    
    return plainText.length > maxLength 
      ? plainText.substring(0, maxLength) + '...' 
      : plainText;
  };

  /* -------------------------------------------------------------------- */
  /* Firebase realtime subscription                                       */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const db = getFirestore();
    const notificationsRef = collection(db, `users/${user.uid}/notifications`);

    const unsubscribe = onSnapshot(
      notificationsRef,
      (snapshot) => {
        setLoadingNotifications(false);

        if (snapshot.empty) {
          setNotifications([]);
          setUnreadCount(0);
          return;
        }

        const fetched = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt ? d.data().createdAt.toDate() : new Date(),
        }));

        fetched.sort((a, b) => b.createdAt - a.createdAt);
        setNotifications(fetched);
        setUnreadCount(fetched.filter((n) => !n.isRead).length);
      },
      (err) => {
        setLoadingNotifications(false);
        message.error("Błąd podczas ładowania powiadomień.");
        console.error(err);
      }
    );

    return () => unsubscribe();
  }, []);

  /* -------------------------------------------------------------------- */
  /* Helpers                                                              */
  /* -------------------------------------------------------------------- */
  const getPageTitle = () => {
    switch (location.pathname) {
      case "/rozliczenia":
        return "Rozliczenia";
      case "/faktury":
        return "Faktury";
      case "/samochod":
        return "Samochód";
      case "/konto":
        return "Konto";
      case "/":
        return "Strona główna";
      case "/pomoc":
         return "Pomoc";
               case "/dokumenty":
         return "Dokumenty";
      default:
        return "Panel Spario";
    }
  };

  const formatDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday)
      return `Dzisiaj o ${date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;
    if (isYesterday)
      return `Wczoraj o ${date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;

    return date.toLocaleString("pl-PL", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric", 
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  /* -------------------------------------------------------------------- */
  /* Notification helpers                                                 */
  /* -------------------------------------------------------------------- */
  const markNotificationAsRead = async (notification) => {
    if (notification.isRead) return;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    
    try {
      const db = getFirestore();
      await updateDoc(doc(db, `users/${user.uid}/notifications`, notification.id), { 
        isRead: true,
        readAt: new Date()
      });
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      message.error("Błąd podczas aktualizacji powiadomień.");
      console.error(err);
    }
  };

  const openNotification = (notification) => {
    setActiveNotification(notification);
    markNotificationAsRead(notification);
    setMenuVisible(false);
  };

  /* -------------------------------------------------------------------- */
  /* Dropdown menu with formatted notifications                           */
  /* -------------------------------------------------------------------- */
  const notificationMenu = (
    <div className="notification-scroll">
      <Menu selectable={false} className="notification-menu">
        <Menu.ItemGroup title="Nie przeczytano">
          {notifications.filter((n) => !n.isRead).length ? (
            notifications
              .filter((n) => !n.isRead)
              .map((n) => (
                <Menu.Item 
                  key={n.id} 
                  className="notification-item unread" 
                  onClick={() => openNotification(n)}
                >
                  <div className="notification-content">
                    <div className="notification-title">
                      {n.title || "Bez tytułu"}
                    </div>
                    <div className="notification-text">
                      {getTextPreview(n.text)}
                    </div>
                    <div className="notification-date">
                      {formatDate(n.createdAt)}
                    </div>
                    {n.hasFormatting && (
                      <div className="notification-formatting-indicator">
                        <span style={{ fontSize: '10px', color: '#1890ff' }}>📝 Sformatowane</span>
                      </div>
                    )}
                  </div>
                </Menu.Item>
              ))
          ) : (
            <Menu.Item className="notification-item empty">
              <div className="notification-empty">
                Brak nieprzeczytanych powiadomień
              </div>
            </Menu.Item>
          )}
        </Menu.ItemGroup>
        
        <Menu.Divider />
        
        <Menu.ItemGroup title="Przeczytano">
          {notifications.filter((n) => n.isRead).length ? (
            notifications
              .filter((n) => n.isRead)
              .slice(0, 10)
              .map((n) => (
                <Menu.Item 
                  key={n.id} 
                  className="notification-item read" 
                  onClick={() => openNotification(n)}
                >
                  <div className="notification-content">
                    <div className="notification-title">
                      {n.title || "Bez tytułu"}
                    </div>
                    <div className="notification-text">
                      {getTextPreview(n.text)}
                    </div>
                    <div className="notification-date">
                      {formatDate(n.createdAt)}
                    </div>
                    {n.hasFormatting && (
                      <div className="notification-formatting-indicator">
                        <span style={{ fontSize: '10px', color: '#8c8c8c' }}>📝</span>
                      </div>
                    )}
                  </div>
                </Menu.Item>
              ))
          ) : (
            <Menu.Item className="notification-item empty">
              <div className="notification-empty">
                Brak powiadomień
              </div>
            </Menu.Item>
          )}
        </Menu.ItemGroup>
      </Menu>
    </div>
  );

  /* -------------------------------------------------------------------- */
  /* Render                                                               */
  /* -------------------------------------------------------------------- */
  return (
    <Header className="app-header" style={{ top: 10 }}>
      {/* Logo */}
      <div className="logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
        <img src={logoSrc} alt="Logo" />
      </div>

      {/* Page title in center */}
      <div className="page-title-center">
        <AnimatePresence mode="wait">
          <motion.span
            key={getPageTitle()}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="page-title-text"
          >
            {getPageTitle()}
          </motion.span>
        </AnimatePresence>
      </div>

      {/* Bell icon with loading animation */}
      <div className="notification-icon">
        <Dropdown
          overlay={notificationMenu}
          trigger={["click"]}
          visible={menuVisible}
          onVisibleChange={setMenuVisible}
          overlayClassName="notification-dropdown"
          placement="bottomRight"
        >
          <motion.div
            animate={loadingNotifications ? { rotate: 360 } : { rotate: 0 }}
            transition={loadingNotifications ? { repeat: Infinity, duration: 1, ease: "linear" } : { duration: 0 }}
            style={{ display: "inline-block" }}
          >
            <Badge 
              count={unreadCount} 
              style={{ boxShadow: "none", color: "#fff" }}
              showZero={false}
            >
              <BellOutlined 
                style={{ 
                  fontSize: 24, 
                  color: unreadCount > 0 ? "#1890ff" : "black", 
                  cursor: "pointer",
                  transition: "color 0.3s ease"
                }} 
              />
            </Badge>
          </motion.div>
        </Dropdown>
      </div>

      {/* Enhanced notification modal with formatting support */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{activeNotification?.title || "Powiadomienie"}</span>
            {activeNotification?.hasFormatting && (
              <span style={{ fontSize: '12px', color: '#1890ff' }}>📝</span>
            )}
            {activeNotification?.isEdited && (
              <span style={{ fontSize: '10px', color: '#faad14' }}>(Edytowane)</span>
            )}
          </div>
        }
        visible={!!activeNotification}
        onCancel={() => setActiveNotification(null)}
        footer={null}
        width={600}
        className="formatted-notification-modal"
      >
        {activeNotification && (
          <div className="notification-modal-content">
            {/* Formatted notification text */}
            <div 
              className="notification-formatted-text"
              style={{ 
                lineHeight: '1.6',
                fontSize: '14px',
                color: '#333',
                marginBottom: 16,
                minHeight: 40,
                padding: 12,
                backgroundColor: '#fafafa',
                borderRadius: 6,
                border: '1px solid #f0f0f0'
              }}
              dangerouslySetInnerHTML={{ 
                __html: formatNotificationText(activeNotification.text) 
              }}
            />
            
            {/* Notification metadata */}
            <div className="notification-metadata" style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              paddingTop: 12,
              borderTop: '1px solid #f0f0f0',
              fontSize: '12px',
              color: '#8c8c8c'
            }}>
              <span>
                Utworzono: {formatDate(activeNotification.createdAt)}
              </span>
              
              {activeNotification.readAt && (
                <span>
                  Przeczytano: {formatDate(new Date(activeNotification.readAt.toDate()))}
                </span>
              )}
              
              {activeNotification.updatedAt && (
                <span>
                  Zaktualizowano: {formatDate(new Date(activeNotification.updatedAt.toDate()))}
                </span>
              )}
            </div>
          </div>
        )}
      </Modal>
    </Header>
  );
};

export default AppHeader;